<script setup lang="ts">
import PdsPagination from '@/components/pds/PdsPagination.vue';
import PdsSegmented from '@/components/pds/PdsSegmented.vue';
import PdsSelect from '@/components/pds/PdsSelect.vue';
import { computed, onBeforeUnmount, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import HistoryFilters from '../components/HistoryFilters.vue';
import HistoryCards from '../components/HistoryCards.vue';
import HistoryTable from '../components/HistoryTable.vue';
import PageBar from '../components/PageBar.vue';
import PageTitle from '../components/PageTitle.vue';
import { PAGE_SIZES, type HistoryStatus } from '../history';
import { usePdsHistoryStore } from '../stores/history.store';

const history = usePdsHistoryStore();

const STATUSES: { value: HistoryStatus; label: string }[] = [
  { value: 'all', label: 'Toutes' },
  { value: 'open', label: 'En cours' },
  { value: 'handed', label: 'Remises' },
  { value: 'cancelled', label: 'Annulées et refusées' },
];
const SIZE_OPTIONS = PAGE_SIZES.map((size) => ({
  value: size,
  label: `${size} par page`,
}));

const route = useRoute();
const cancelledView = computed(() => history.query.status === 'cancelled');

// Le menu ouvre le journal déjà filtré (`?statut=cancelled`) ; sans lien, on garde les filtres en cours.
onMounted(() => {
  const wanted = STATUSES.find(({ value }) => value === route.query.statut);
  return wanted ? history.setFilters({ status: wanted.value }) : history.load();
});
onBeforeUnmount(() => history.stopTimers());
</script>

<template>
  <PageBar tall>
    <PageTitle
      :title="
        cancelledView
          ? 'Commandes remboursées et refusées'
          : 'Historique des commandes'
      "
      :subtitle="`${history.total} commande${history.total > 1 ? 's' : ''}${cancelledView ? '' : ' · filtrées côté serveur'}`"
    />
  </PageBar>

  <main
    class="no-scrollbar flex min-h-0 flex-1 flex-col gap-4 overflow-auto px-4 py-5 sm:px-5"
  >
    <!-- Masqués pour l'instant sur la liste des commandes remboursées et refusées. -->
    <div v-if="!cancelledView" class="flex flex-wrap items-center gap-3">
      <PdsSegmented
        :model-value="history.query.status"
        :options="STATUSES"
        @update:model-value="history.setFilters({ status: $event })"
      />
      <input
        type="search"
        placeholder="N°, nom, e-mail, téléphone"
        aria-label="Rechercher une commande"
        class="bg-carte text-encre h-[58px] min-w-[240px] flex-1 rounded-[20px] px-4 text-[16px] shadow-[inset_0_0_0_1px_var(--pds-filet-fort)] outline-none focus:shadow-[inset_0_0_0_2px_var(--pds-encre)]"
        :value="history.query.q"
        @input="history.setSearch(($event.target as HTMLInputElement).value)"
      />
    </div>
    <HistoryFilters v-if="!cancelledView" />

    <p
      v-if="history.status === 'error'"
      role="alert"
      class="bg-alerte-doux text-alerte-texte flex items-center justify-between gap-3 rounded-[16px] px-4 py-3 text-[14px]"
    >
      Impossible de charger les commandes.
      <button
        type="button"
        class="cursor-pointer font-semibold underline"
        @click="history.load()"
      >
        Réessayer
      </button>
    </p>
    <p
      v-else-if="history.status === 'ready' && !history.rows.length"
      class="text-encre-douce py-10 text-center text-[16px]"
    >
      Aucune commande ne correspond à ces filtres.
    </p>
    <div
      v-else
      :class="history.status === 'loading' ? 'opacity-60' : ''"
      :aria-busy="history.status === 'loading'"
    >
      <HistoryCards v-if="cancelledView" :rows="history.rows" />
      <HistoryTable v-else :rows="history.rows" />
    </div>

    <footer
      class="flex flex-wrap items-center justify-between gap-3"
      data-testid="history-footer"
    >
      <p class="text-encre-douce text-[14px]">
        {{ history.firstShown }}–{{ history.lastShown }} sur {{ history.total }}
      </p>
      <PdsPagination
        :page="history.query.page"
        :page-count="history.pageCount"
        @change="history.setPage($event)"
      />
      <PdsSelect
        id="history-page-size"
        label="Affichage"
        :options="SIZE_OPTIONS"
        :model-value="history.query.pageSize"
        @update:model-value="history.setPageSize(Number($event))"
      />
    </footer>
  </main>
</template>
